import { isUserQuestionItem } from "@/components/features/ai-chat/chat-user-question";
import type {
  AiChatGuardianReview,
  AiChatTimelineItem,
  AiChatLayoutItem,
} from "@/lib/types/ai-chat";
import { layoutFromItem } from "./chat-layout";
import { replaceEqualDeep } from "@tanstack/react-query";

export type TimelineAnchor = { itemId: string; viewportOffset: number };
export type TimelineRow = {
  id: string;
  layout: AiChatLayoutItem;
  item?: AiChatTimelineItem;
  anchorItemIds: string[];
};
type ReviewDetails = Readonly<Record<string, AiChatGuardianReview | undefined>>;

/** Keep the bubble mounted when the server confirms an optimistic message. */
export function timelineRowKey(row: TimelineRow) {
  const requestId = row.item?.payload.clientRequestId;
  return row.layout.kind === "user_message" && typeof requestId === "string"
    ? `request:${requestId}`
    : row.id;
}

export function isGuardianActivity(item: AiChatTimelineItem) {
  return (
    item.kind === "tool_activity" &&
    (item.payload.isGuardianActivity === true ||
      item.payload.toolName === "inspect_lawworks_data")
  );
}

export function isHiddenTimelineItem(
  item:
    | (Pick<AiChatTimelineItem, "kind" | "status"> & {
        guardianReviewId?: string | null;
      })
    | undefined,
  details: ReviewDetails = {},
) {
  if (
    item?.status === "superseded" &&
    (item.kind === "assistant_message" || item.kind === "commentary")
  )
    return true;
  if (item?.kind !== "approval_request") return false;
  const review = item.guardianReviewId
    ? details[item.guardianReviewId]
    : undefined;
  return (
    Boolean(
      review &&
        review.status !== "pending_guardian" &&
        review.status !== "awaiting_human",
    ) || item.status !== "awaiting_human"
  );
}

export function timelineRows(
  items: AiChatTimelineItem[],
  layout: AiChatLayoutItem[] | undefined = undefined,
  details: ReviewDetails = {},
  previous: TimelineRow[] = [],
): TimelineRow[] {
  const loaded = new Map(items.map((item) => [item.id, item]));
  const entries = new Map(layout?.map((entry) => [entry.id, entry]));
  for (const item of items)
    if (!entries.has(item.id)) entries.set(item.id, layoutFromItem(item));
  const rows: TimelineRow[] = [];
  const leadingAliases: string[] = [];
  for (const entry of [...entries.values()].sort(
    (a, b) => a.sequence - b.sequence,
  )) {
    const candidate = loaded.get(entry.id);
    if (
      isHiddenTimelineItem(
        { ...entry, guardianReviewId: candidate?.guardianReviewId },
        details,
      ) ||
      (candidate && isGuardianActivity(candidate))
    ) {
      if (rows.length) rows[rows.length - 1].anchorItemIds.push(entry.id);
      else leadingAliases.push(entry.id);
      continue;
    }
    const candidateLayout = candidate ? layoutFromItem(candidate) : undefined;
    const candidateIsCurrent =
      candidateLayout &&
      candidateLayout.revision >= entry.revision &&
      candidateLayout.endSequence >= entry.endSequence;
    const rowLayout = candidateIsCurrent
      ? {
          ...candidateLayout,
          textCharacters: Math.max(
            candidateLayout.textCharacters,
            entry.textCharacters,
          ),
          textLines: Math.max(candidateLayout.textLines, entry.textLines),
        }
      : entry;
    rows.push({
      id: entry.id,
      layout: rowLayout,
      item: candidateIsCurrent ? candidate : undefined,
      anchorItemIds: [entry.id, ...leadingAliases.splice(0)],
    });
  }
  const byId = new Map(previous.map((row) => [row.id, row]));
  return rows.map((row) => replaceEqualDeep(byId.get(row.id), row));
}

export function isActivityRow(row: TimelineRow) {
  return (
    row.layout.kind === "tool_activity" &&
    Boolean(
      row.layout.activity.activitySummary ||
        Array.isArray(row.layout.activity.artifacts) ||
        Array.isArray(row.layout.activity.widgets) ||
        (row.item && !isUserQuestionItem(row.item)),
    )
  );
}

export function estimatedRowHeight(row: TimelineRow, width = 800) {
  const layout = row.layout;
  if (layout.kind === "approval_request") return 180;
  if (layout.kind === "tool_activity")
    return Array.isArray(layout.activity.widgets)
      ? 460 * layout.activity.widgets.length
      : layout.activity.activitySummary
        ? 44
        : Array.isArray(layout.activity.artifacts)
          ? 44 + layout.activity.artifacts.length * 66
          : 120;
  const user = layout.kind === "user_message";
  const columns = Math.max(
    20,
    Math.floor(((width - 64) * (user ? 0.75 : 1)) / 7),
  );
  return Math.max(
    user ? 72 : 56,
    (Math.ceil(layout.textCharacters / columns) + layout.textLines) * 24 +
      (user ? 48 : 24),
  );
}

export function rowContainsItem(row: TimelineRow, itemId: string) {
  return row.anchorItemIds.includes(itemId);
}

export function renderedAnchorElement(element: HTMLDivElement, itemId: string) {
  return [
    ...element.querySelectorAll<HTMLElement>("[data-timeline-item-ids]"),
  ].find((candidate) =>
    candidate.dataset.timelineItemIds?.split(" ").includes(itemId),
  );
}

export function removedVisibleApproval(
  previous: TimelineRow[],
  current: TimelineRow[],
) {
  return previous.some(
    (row) =>
      row.layout.kind === "approval_request" &&
      !current.some((candidate) => candidate.id === row.id),
  );
}
